import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot, doc, updateDoc, orderBy } from 'firebase/firestore';

// Estilos mejorados (adaptados de AdminPromotionBookings)
const styles = {
    primaryColor: '#3498db',
    accentColor: '#2ecc71',
    dangerColor: '#e74c3c',
    textColor: '#2c3e50',
    lightGray: '#ecf0f1',
    mediumGray: '#bdc3c7',
    darkGray: '#7f8c8d',

    container: { 
        paddingTop: '4rem', 
        paddingLeft: '1rem', 
        paddingRight: '2rem', 
        paddingBottom: '2rem', 
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        color: '#2c3e50'
    },
    header: { 
        fontSize: '2.5rem', 
        fontWeight: '700', 
        color: '#2c3e50',
        marginBottom: '1.5rem', 
        paddingBottom: '0.5rem', 
        borderBottom: `2px solid #bdc3c7`
    },
    controls: { 
        marginBottom: '1.5rem', 
        display: 'flex', 
        gap: '15px', 
        alignItems: 'center',
        padding: '15px',
        backgroundColor: '#ecf0f1',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    },
    select: { 
        padding: '10px', 
        borderRadius: '5px', 
        border: `1px solid #bdc3c7`,
        backgroundColor: 'white',
        color: '#2c3e50',
        cursor: 'pointer'
    },
    bookingTable: { 
        width: '100%', 
        borderCollapse: 'collapse', 
        marginTop: '1rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        borderRadius: '8px',
        overflow: 'hidden'
    },
    th: { 
        border: `1px solid #bdc3c7`,
        padding: '12px 8px', 
        backgroundColor: '#2c3e50',
        color: 'white', 
        textAlign: 'left',
        fontWeight: '600'
    },
    td: { 
        border: `1px solid #bdc3c7`,
        padding: '10px 8px', 
        color: '#2c3e50'
    },
    button: { 
        padding: '8px 12px', 
        cursor: 'pointer', 
        color: 'white', 
        border: 'none', 
        borderRadius: '5px', 
        fontWeight: 'bold',
        transition: 'background-color 0.2s ease'
    },
    statusBadge: {
        padding: '5px 10px',
        borderRadius: '15px',
        fontWeight: 'bold',
        color: 'white',
        display: 'inline-block',
        minWidth: '80px',
        textAlign: 'center'
    },
    statusApproved: { backgroundColor: '#2ecc71' },
    statusPending: { backgroundColor: '#f39c12' },
    statusDeclined: { backgroundColor: '#e74c3c' },
    actionButtonConfirm: { backgroundColor: '#2ecc71', '&:hover': { backgroundColor: '#27ae60' } },
    actionButtonReject: { backgroundColor: '#e74c3c', '&:hover': { backgroundColor: '#c0392b' } },
};

const AdminReservationsManagement = () => {
  const [orders, setOrders] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDate, setFilterDate] = useState(''); // For filtering by date
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const navigate = useNavigate();

  useEffect(() => {
    let q = collection(db, "deliveryOrders");

    if (filterStatus !== 'all') {
      q = query(q, where("status", "==", filterStatus));
    }
    // TODO: Implement date filtering
    q = query(q, orderBy(sortBy, sortOrder));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedOrders = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log("Fetched delivery orders:", fetchedOrders);
      setOrders(fetchedOrders);
    }, (error) => {
      console.error("Error fetching delivery orders: ", error);
      alert("Error al cargar los pedidos: " + error.message);
    });

    return () => unsubscribe();
  }, [filterStatus, sortBy, sortOrder]);

  const handleUpdateStatus = async (orderId, newStatus) => {
    if (!window.confirm(`¿Estás seguro de que quieres cambiar el estado a "${newStatus}"?`)) {
        return;
    }
    try {
        const orderRef = doc(db, "deliveryOrders", orderId);
        await updateDoc(orderRef, {
            status: newStatus,
            updatedAt: new Date().toISOString(),
        });
        alert(`¡Pedido ${newStatus} en Firestore!`);
    } catch (error) {
        console.error(`Error updating order to ${newStatus}: `, error);
        alert(`Error al actualizar el pedido: " + error.message`);
    }
};


  const getStatusStyle = (status) => {
    const baseStyle = styles.statusBadge;
    switch (status) {
      case 'approved':
      case 'pagado':
        return { ...baseStyle, ...styles.statusApproved };
      case 'pending_transfer':
      case 'pending':
        return { ...baseStyle, ...styles.statusPending };
      case 'declined':
      case 'rechazado':
        return { ...baseStyle, ...styles.statusDeclined };
      default: return baseStyle;
    }
  };

  const formatCartItems = (cartItems) => {
    if (!cartItems || cartItems.length === 0) return 'N/A';
    return cartItems.map(item => `${item.name} (x${item.quantity})`).join(', ');
  };

  const getPaymentMethodName = (method) => {
      if (method === 'bankTransfer') return 'Transferencia Bancaria';
      if (method === 'wompi') return 'PSE';
      return method;
  }

  const getStatusName = (status) => {
      switch (status) {
          case 'pending_transfer':
          case 'pending':
              return 'Pendiente';
          case 'approved':
          case 'pagado':
              return 'Pagado';
          case 'declined':
          case 'rechazado':
              return 'Rechazado';
          default:
              return status;
      }
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.header}>Gestión de Pedidos a Domicilio</h1>

      <div style={styles.controls}>
        <label>Filtrar por estado:</label>
        <select style={styles.select} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="all">Todos</option>
          <option value="approved">Aprobado</option>
          <option value="pending">Pendiente</option>
          <option value="declined">Rechazado</option>
        </select>

        <label>Ordenar por:</label>
        <select style={styles.select} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="createdAt">Fecha de Creación</option>
          <option value="customerData.fullName">Nombre del Cliente</option>
          <option value="totalPrice">Monto Total</option>
        </select>

        <select style={styles.select} value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
          <option value="desc">Descendente</option>
          <option value="asc">Ascendente</option>
        </select>
      </div>

      {orders.length === 0 ? (
        <p>No hay pedidos a domicilio.</p>
      ) : (
        <table style={styles.bookingTable}>
          <thead>
            <tr>
              <th style={{...styles.th, width: '20%'}}>Cliente</th>
              <th style={styles.th}>Fecha de pedido</th>
              <th style={styles.th}>Teléfono</th>
              <th style={styles.th}>Tipo de pedido</th>
              <th style={styles.th}>Dirección de entrega</th>
              <th style={{...styles.th, width: '30%'}}>Detalles del pedido</th>
              <th style={styles.th}>Monto Total</th>
              <th style={styles.th}>Método Pago</th>
              <th style={styles.th}>Estado de pago</th>
              <th style={styles.th}>Acción</th>
              <th style={styles.th}>ID del pedido</th>
            </tr>
          </thead>
          <tbody>
            {orders.map(order => (
              <tr key={order.id}>
                <td style={styles.td}>{order.customerData ? order.customerData.fullName : 'N/A'}</td>
                <td style={styles.td}>{new Date(order.createdAt && typeof order.createdAt.toDate === 'function' ? order.createdAt.toDate() : order.createdAt).toLocaleString()}</td>
                <td style={styles.td}>{order.customerData ? order.customerData.phoneNumber : 'N/A'}</td>
                <td style={styles.td}>{order.deliveryOption === 'domicilio' ? 'Domicilio' : 'Recoger'}</td>
                <td style={styles.td}>
                  {order.deliveryOption === 'domicilio' ? order.address : order.sede ? order.sede.name : 'N/A'}
                </td>
                <td style={styles.td}>{formatCartItems(order.cartItems)}</td>
                <td style={styles.td}>${order.precioTotal.toLocaleString('es-CO')}</td>
                <td style={styles.td}>{getPaymentMethodName(order.paymentMethod)}</td>
                <td style={styles.td}>
                  <span style={getStatusStyle(order.status)}>
                    {getStatusName(order.status)}
                  </span>
                </td>
                <td style={styles.td}>
                  {order.paymentMethod === 'bankTransfer' && (order.status === 'pending_transfer' || order.status === 'pending') ? (
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <select onChange={(e) => handleUpdateStatus(order.id, e.target.value)} defaultValue="">
                        <option value="" disabled>Seleccionar...</option>
                        <option value="pagado">Aprobar</option>
                        <option value="rechazado">Rechazar</option>
                      </select>
                    </div>
                  ) : (
                    '-'
                  )}
                </td>
                <td style={styles.td}>{order.id}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminReservationsManagement;
